import type { components } from "@/api/schema";
import type { ProductSummaryItem } from "@/components/product-summary-list";

export type ProjectOrderLine =
  components["schemas"]["InternalOrderLineItemRead"];
export type ProjectPurchaseOrder =
  components["schemas"]["ProjectPurchaseOrderRead"];
export type ProjectFinancialOrder = Pick<
  components["schemas"]["InternalOrderRead"],
  "financials"
>;

export type ProjectFinancials = {
  revenue: number | null;
  net: number | null;
  profit: number | null;
  margin: number | null;
};

function finiteNumber(value: string | number | null | undefined) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// additionalNet is project cost beyond its orders (install, storage, freight
// invoices filed on the project); null while it is unknown, which leaves Net,
// Profit and Margin unknown too.
export function calculateProjectFinancials(
  orders: ProjectFinancialOrder[],
  additionalNet: number | null = 0,
): ProjectFinancials {
  // Each order's financials already account for refunds, edits, cancelled POs and
  // manufacturer bills; the project only adds them up.
  let revenue = 0;
  let net = 0;
  let netKnown = true;

  for (const order of orders) {
    revenue += finiteNumber(order.financials.revenue) ?? 0;
    const orderNet = finiteNumber(order.financials.net);
    if (orderNet == null) netKnown = false;
    else net += orderNet;
  }

  const knownRevenue = revenue;
  const knownNet = netKnown && additionalNet != null ? net + additionalNet : null;
  const knownProfit =
    knownRevenue != null && knownNet != null ? knownRevenue - knownNet : null;
  return {
    revenue: knownRevenue,
    net: knownNet,
    profit: knownProfit,
    margin:
      knownRevenue != null && knownProfit != null && knownRevenue > 0
        ? (knownProfit / knownRevenue) * 100
        : null,
  };
}

function record(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function isManualProjectOrderLine(line: ProjectOrderLine) {
  const payload = record(line.source_payload);
  const dropship = record(payload?.dropship);
  return (
    payload?.procurement_mode === "manual" ||
    payload?.disposition === "internal_only" ||
    payload?.po_treatment === "internal_only" ||
    payload?.dropship_disposition === "internal_only" ||
    dropship?.disposition === "internal_only"
  );
}

export function groupProjectOrderLines(
  purchaseOrders: ProjectPurchaseOrder[],
  lineItems: ProjectOrderLine[],
) {
  const purchaseOrderById = new Map(purchaseOrders.map((po) => [po.id, po]));
  const purchaseOrdersByManufacturerId = new Map<
    number,
    ProjectPurchaseOrder[]
  >();
  const purchaseOrdersByManufacturerUuid = new Map<
    string,
    ProjectPurchaseOrder[]
  >();
  for (const po of purchaseOrders) {
    const byId = purchaseOrdersByManufacturerId.get(po.manufacturer_id) ?? [];
    byId.push(po);
    purchaseOrdersByManufacturerId.set(po.manufacturer_id, byId);
    if (po.manufacturer?.uuid) {
      const byUuid =
        purchaseOrdersByManufacturerUuid.get(po.manufacturer.uuid) ?? [];
      byUuid.push(po);
      purchaseOrdersByManufacturerUuid.set(po.manufacturer.uuid, byUuid);
    }
  }

  const lineById = new Map(lineItems.map((line) => [line.id, line]));
  const manualById = new Map<number, boolean>();
  const resolvingManual = new Set<number>();
  const manual = (line: ProjectOrderLine): boolean => {
    const cached = manualById.get(line.id);
    if (cached != null) return cached;
    if (resolvingManual.has(line.id)) return isManualProjectOrderLine(line);
    resolvingManual.add(line.id);
    const parent = line.parent_line_item_id
      ? lineById.get(line.parent_line_item_id)
      : undefined;
    const result =
      isManualProjectOrderLine(line) || (parent ? manual(parent) : false);
    resolvingManual.delete(line.id);
    manualById.set(line.id, result);
    return result;
  };

  const poIdByLineId = new Map<number, number | null>();
  const resolvingPO = new Set<number>();
  const purchaseOrderId = (line: ProjectOrderLine): number | null => {
    if (poIdByLineId.has(line.id)) return poIdByLineId.get(line.id) ?? null;
    if (resolvingPO.has(line.id)) return null;
    resolvingPO.add(line.id);
    const persistedPO = line.purchase_order_id
      ? purchaseOrderById.get(line.purchase_order_id)
      : undefined;
    const parent = line.parent_line_item_id
      ? lineById.get(line.parent_line_item_id)
      : undefined;
    const fallbackCandidates = new Map<number, ProjectPurchaseOrder>();
    if (line.manufacturer_id) {
      for (const po of purchaseOrdersByManufacturerId.get(
        line.manufacturer_id,
      ) ?? []) {
        fallbackCandidates.set(po.id, po);
      }
    }
    if (line.product?.manufacturer_uuid) {
      for (const po of purchaseOrdersByManufacturerUuid.get(
        line.product.manufacturer_uuid,
      ) ?? []) {
        fallbackCandidates.set(po.id, po);
      }
    }
    const fallbackPO =
      fallbackCandidates.size === 1
        ? [...fallbackCandidates.values()][0]
        : undefined;
    const result =
      line.purchase_order_id != null
        ? (persistedPO?.id ?? null)
        : ((parent ? purchaseOrderId(parent) : null) ?? fallbackPO?.id ?? null);
    resolvingPO.delete(line.id);
    poIdByLineId.set(line.id, result);
    return result;
  };

  const byPurchaseOrderId = new Map<number, ProjectOrderLine[]>();
  const manualItems: ProjectOrderLine[] = [];
  const unassignedItems: ProjectOrderLine[] = [];
  for (const line of lineItems) {
    if (manual(line)) {
      manualItems.push(line);
      continue;
    }
    const poId = purchaseOrderId(line);
    if (poId == null) {
      unassignedItems.push(line);
      continue;
    }
    const group = byPurchaseOrderId.get(poId) ?? [];
    group.push(line);
    byPurchaseOrderId.set(poId, group);
  }

  return { byPurchaseOrderId, manualItems, unassignedItems };
}

/** An order line as the small product list with photos shows it. */
export function toProductSummaryItem(li: ProjectOrderLine): ProductSummaryItem {
  return {
    id: li.id,
    title: li.title,
    quantity: li.quantity,
    productUuid: li.product?.uuid,
    imageUrl: li.product?.image_url,
    manufacturerName: li.product?.manufacturer_name,
    manufacturerUuid: li.product?.manufacturer_uuid,
  };
}
